import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';

function useCompletionDismissal(householdId: string, trackerId?: string) {
  const storageKey = `${LOCAL_STORAGE_KEYS.HOUSEHOLD_SETUP_COMPLETION_DISMISSED}:${householdId}:${trackerId ?? ''}`;
  const [storedDismissal, persistDismissal] = useLocalStorage(
    storageKey,
    false
  );
  const [dismissedKey, setDismissedKey] = useState<string>();

  const dismiss = () => {
    if (!trackerId) return;
    // react-use does not update its state if the storage write throws.
    setDismissedKey(storageKey);
    persistDismissal(true);
  };

  return {
    dismissed:
      !!trackerId && (dismissedKey === storageKey || storedDismissal === true),
    dismiss,
  };
}

type RefreshState = {
  scope: string;
  status: 'refreshing' | 'ready' | 'failed';
  hasSucceeded: boolean;
};

/**
 * Refresh household data after setup workflows finish so the overview reflects
 * their writes before showing completion. Remember completion-hero dismissal
 * across reloads, scoped to one HouseholdOnboardingCompletion workflow.
 */
export function useHouseholdSetupCompletion({
  householdId,
  onboardingCompletionTrackerId,
  overviewRefreshTrackerIds,
  refreshOverview,
}: {
  householdId: string;
  onboardingCompletionTrackerId?: string;
  overviewRefreshTrackerIds: string[];
  refreshOverview: () => Promise<unknown>;
}) {
  const { reportError } = useReportError();
  const dismissal = useCompletionDismissal(
    householdId,
    onboardingCompletionTrackerId
  );

  // Identifies the completed/failed workflows whose household data we refreshed.
  // Sorting avoids refetches when the same trackers arrive in a different order.
  const refreshKey = JSON.stringify([
    householdId,
    [...overviewRefreshTrackerIds].sort(),
  ]);
  const [refreshState, setRefreshState] = useState<RefreshState>();
  const requestVersion = useRef(0);
  const hasTrackersToRefresh = overviewRefreshTrackerIds.length > 0;

  const runRefresh = useCallback(async () => {
    if (!hasTrackersToRefresh) return;
    const version = ++requestVersion.current;
    setRefreshState((previous) => ({
      scope: refreshKey,
      status: 'refreshing',
      hasSucceeded: previous?.scope === refreshKey && previous.hasSucceeded,
    }));
    try {
      // Use only this overview's canonical refresh, not all active app queries.
      await refreshOverview();
      if (version !== requestVersion.current) return;
      setRefreshState({
        scope: refreshKey,
        status: 'ready',
        hasSucceeded: true,
      });
    } catch (error: unknown) {
      if (version !== requestVersion.current) return;
      setRefreshState({
        scope: refreshKey,
        status: 'failed',
        hasSucceeded: false,
      });
      reportError(
        'Failed to refresh household setup data',
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }, [refreshKey, refreshOverview, hasTrackersToRefresh, reportError]);

  useEffect(() => {
    void runRefresh();
    return () => {
      // Ignore responses from a previous scope or an unmounted overview.
      requestVersion.current += 1;
    };
  }, [runRefresh]);
  useRefetchOnAppResume(runRefresh, {
    label: `HouseholdSetupData:${householdId}`,
    enabled: hasTrackersToRefresh,
  });

  const currentRefresh =
    refreshState?.scope === refreshKey ? refreshState : undefined;
  return {
    ...dismissal,
    refreshing:
      hasTrackersToRefresh &&
      (!currentRefresh || currentRefresh.status === 'refreshing'),
    // A resume request must not hide a hero whose workflow writes were already read.
    readyForCompletion: currentRefresh?.hasSucceeded ?? false,
    refreshFailed: currentRefresh?.status === 'failed',
  };
}
